import React, { useState } from 'react';
import { Box, List, Button, Grid2 as Grid } from '@mui/material';
import { Link } from 'react-router-dom';
import { toastr } from '../../services/toast-service';
import { t } from 'i18next';
import CartDropdownHeader from '../CartDropdown/CartDropdownHeader';
import CartDropdownItem from '../CartDropdown/CartDropdownItem';
import { Logger } from '../../core/logger/Logger';
import { StrapiBasketItem } from '../../core/models/strapi-basket-item.model';
import { basketService, calculateTotalFromVendorEntries } from '../../services/basket-service';
import { StrapiProduct } from '../../core/models/strapi-product.model';
import './BasketItems.scss';
import { useAtomValue } from 'jotai';
import { basketAtom } from '../../store/jotai/atoms/basket.atom';
import { regionStore } from '../../store/jotai/store';
import { ShoppingCartCheckout } from '@mui/icons-material';
import { StrapiShippingRate, SHIPPING_TYPES } from '../../core/models/strapi-shipping-rate.model';
import { VendorBasketEntry } from '../../models/basket.model';
import { SendcloudSelection } from '../Checkout/SendcloudShippingSelector';

interface BasketProps {
  toggleCart?: () => void;
  showButton?: boolean;
  button?: React.ReactNode;
  isLoading?: (arg: boolean) => void;
  sendcloudSelections?: Record<string, SendcloudSelection>;
}

const BasketItems = ({
  toggleCart,
  showButton = true,
  button,
  isLoading = () => {},
  sendcloudSelections
}: BasketProps) => {
  const [loading, setLoading] = useState(false);
  const basket = useAtomValue(basketAtom, { store: regionStore });

  const changeQuantity = (
    id: number,
    quantity: number,
    basketItem: StrapiBasketItem,
    product: StrapiProduct
  ) => {
    setLoading(true);
    isLoading(true);
    basketService
      .updateBasketItemQuantity(id, { ...basketItem, quantity })
      .then(() => {
        toastr.success(
          t('basket.updated'),
          t('basket.productUpdated', { productTitle: product.title })
        );
      })
      .catch((error) => {
        const responseData = error?.response?.data;

        if (error?.response?.status === 409 && responseData?.error === 'QUANTITY_ADJUSTED') {
          basketService.getBasket().catch(() => {});
          const adjusted = responseData.adjustedQuantity;
          if (adjusted === 0) {
            toastr.warning(
              t('basket.updated'),
              t('basket.productUnavailable', {
                productTitle: product.title,
                defaultValue: `"${product.title}" ist aktuell nicht verfügbar.`
              })
            );
          } else {
            toastr.warning(
              t('basket.updated'),
              t('basket.quantityAdjusted', {
                productTitle: product.title,
                availableQuantity: adjusted,
                defaultValue: `"${product.title}": Menge wurde auf ${adjusted} reduziert (maximal verfügbar).`
              })
            );
          }
          return;
        }

        const errorMessage = responseData?.error?.message || '';
        const match = errorMessage.includes('AVAILABLE_QUANTITY_EXCEEDED');
        const availableQuantity = errorMessage.split('AVAILABLE_QUANTITY_EXCEEDED_')[1];
        if (match) {
          basketService.getBasket().then();
          toastr.error(
            t('errors.generalError'),
            t('basket.quantityExceeded', { quantity, availableQuantity })
          );
        } else {
          toastr.error(t('errors.generalError'), t('errors.pleaseRetryLater'));
        }
        Logger.error(error);
      })
      .finally(() => {
        setLoading(false);
        isLoading(false);
      });
  };

  const deleteBasketItem = (id: number, product: StrapiProduct) => {
    setLoading(true);
    isLoading(true);
    basketService
      .deleteBasketItem(id)
      .then(() => {
        toastr.success(
          t('basket.removed'),
          t('basket.productRemoved', { productTitle: product.title })
        );
      })
      .catch((error) => {
        toastr.error(t('errors.generalError'), t('errors.pleaseRetryLater'));
        Logger.error(error);
      })
      .finally(() => {
        setLoading(false);
        isLoading(false);
      });
  };

  const productUnavailable = (product: StrapiProduct) =>
    product.availableQuantity === 0 || product.inactive || product.requestOnly;

  /**
   * Determine effective shipping for a vendor group.
   * Priority: freeShipping (if minOrderAmount met) > flatrate > per-product rates
   */
  const getVendorShippingInfo = (vendor: VendorBasketEntry) => {
    // Every item in the group shares one vendor, so the first item's rates speak for the whole group.
    const vendorShippingRates: StrapiShippingRate[] =
      vendor.items[0]?.product?.vendor?.shipping_rates || [];

    const freeShippingRate = vendorShippingRates.find(
      (r) => r.shippingType === SHIPPING_TYPES.FREE_SHIPPING
    );
    const flatrateRate = vendorShippingRates.find(
      (r) => r.shippingType === SHIPPING_TYPES.FLATRATE
    );

    // Calculate vendor product subtotal (without shipping) for freeShipping threshold check
    const productSubtotal = vendor.items
      .filter((item) => !productUnavailable(item.product))
      .reduce((acc, item) => acc + item.quantity * item.product.price, 0);

    // Free shipping overrides everything if minOrderAmount is met
    if (freeShippingRate) {
      const minAmount = freeShippingRate.minOrderAmount || 0;
      if (minAmount === 0 || productSubtotal >= minAmount) {
        return { type: SHIPPING_TYPES.FREE_SHIPPING, rate: freeShippingRate, productSubtotal };
      }
    }

    // Flatrate overrides per-product shipping
    if (flatrateRate) {
      return { type: SHIPPING_TYPES.FLATRATE, rate: flatrateRate, productSubtotal };
    }

    // Default: per-product shipping
    return { type: 'product' as const, rate: null, productSubtotal };
  };

  return (
    <>
      <Box className='basket-content'>
        {!basket?.basket_items.length ? (
          <p className='empty-basket'>{t('basket.emptyBasket')}</p>
        ) : (
          basket.basket_items.map((vendor, index) => (
            <Box key={vendor.id}>
              <CartDropdownHeader
                className='dropdown-cart-top-header'
                title={vendor.title}
                image={vendor.logoUrl}
                imageAlt={vendor.title}
                linkUrl={`/haendler/${vendor.urlSegment}`}
                linkText={t('basket.viewVendor')}
                toggleCart={toggleCart}
              />
              {vendor?.items.map((item, index) => (
                <List key={index} className='border-top'>
                  <CartDropdownItem
                    product={item.product}
                    quantity={item.quantity}
                    toggleCart={toggleCart}
                    disabled={loading}
                    onDelete={() => deleteBasketItem(item.id, item.product)}
                    onChangeQuantity={(quantity) =>
                      changeQuantity(item.id, quantity, item, item.product)
                    }
                  />
                </List>
              ))}
              {vendor?.items?.length && (
                <>
                  <Box sx={{ mb: 1 }} key={index} className='shipping-rates-container border-top'>
                    <Box>
                      <b>{vendor.title} - Versandkosten:</b>
                    </Box>
                    {(() => {
                      const scSelection = sendcloudSelections?.[String(vendor.id)];
                      if (scSelection) {
                        return (
                          <Box className='shipping-rates'>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              <Grid size={{ xs: 8 }}>{scSelection.methodName}</Grid>
                              <Grid size={{ xs: 4 }} className='shipping-rate-amount'>
                                &euro; {scSelection.price.toFixed(2)}
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      }

                      // Sendcloud vendor without selection.
                      // If `sendcloudSelections` was passed in we're on the checkout page itself
                      // and the user still needs to pick an option; otherwise (basket sidebar etc.)
                      // shipping will be calculated once they reach checkout.
                      const isSendcloudVendor =
                        vendor.items[0]?.product?.vendor?.useSendcloudShipping;
                      if (isSendcloudVendor) {
                        const isOnCheckout = !!sendcloudSelections;
                        return (
                          <Box className='shipping-rates'>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              <Grid size={{ xs: 12 }}>
                                <small>
                                  {isOnCheckout
                                    ? t(
                                        'product.shipping.selectShippingOption',
                                        'Bitte Versandart auswählen'
                                      )
                                    : t(
                                        'product.shipping.calculatedAtCheckout',
                                        'Wird beim Checkout berechnet'
                                      )}
                                </small>
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      }

                      const shippingInfo = getVendorShippingInfo(vendor);

                      if (shippingInfo.type === SHIPPING_TYPES.FREE_SHIPPING) {
                        return (
                          <Box className='shipping-rates'>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              <Grid size={{ xs: 8 }}>
                                {shippingInfo.rate?.title || 'Kostenloser Versand'}
                              </Grid>
                              <Grid size={{ xs: 4 }} className='shipping-rate-amount'>
                                Kostenlos
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      }

                      if (shippingInfo.type === SHIPPING_TYPES.FLATRATE && shippingInfo.rate) {
                        return (
                          <Box className='shipping-rates'>
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              <Grid size={{ xs: 8 }}>
                                {shippingInfo.rate.title || 'Pauschalversand'}
                              </Grid>
                              <Grid size={{ xs: 4 }} className='shipping-rate-amount'>
                                € {shippingInfo.rate.rate.toFixed(2)}
                              </Grid>
                            </Grid>
                          </Box>
                        );
                      }

                      // Per-product shipping rates
                      return vendor.items.map(
                        (item, idx) =>
                          item?.product?.shipping_rates[0]?.rate !== undefined && (
                            <Box
                              sx={productUnavailable(item.product) ? { opacity: '0.5' } : {}}
                              key={idx}
                              className='shipping-rates'>
                              <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid size={{ xs: 8 }}>
                                  {item.quantity} x {item.product.shipping_rates[0].title}
                                </Grid>
                                <Grid size={{ xs: 4 }} className='shipping-rate-amount'>
                                  € {item.product.shipping_rates[0].rate.toFixed(2)}
                                </Grid>
                              </Grid>
                            </Box>
                          )
                      );
                    })()}
                  </Box>
                </>
              )}
            </Box>
          ))
        )}
      </Box>
      {!!basket?.basket_items.length && (
        <Box className='dropdown-cart-footer border-top'>
          <Box>
            <h4>
              Gesamt: &euro;{' '}
              {(() => {
                const total = basket?.basket_items?.length
                  ? calculateTotalFromVendorEntries(basket.basket_items, sendcloudSelections)
                  : 0;
                return total >= 0 ? total.toFixed(2) : '--';
              })()}
            </h4>
            <small className='text-black'>inkl. MwSt</small>
          </Box>
          {showButton && (
            <Box>
              <Button
                className='checkout-button'
                component={Link}
                to={'/checkout'}
                onClick={toggleCart}
                variant='contained'
                disabled={loading || !basket?.basket_items.length || !basket?.total}
                color='primary'>
                <ShoppingCartCheckout fontSize='small' sx={{ mr: 1 }} />
                Jetzt bestellen
              </Button>
            </Box>
          )}
          {button && <Box>{button}</Box>}
        </Box>
      )}
    </>
  );
};

export default BasketItems;
